import { useState, useEffect } from 'react'
import { LoginIcon } from '../components/icons/LoginIcon'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../auth/useAuth'
import { getTenantFromHostname } from '../lib/tenant'
import { ThemeToggleButton } from '../components/ThemeToggleButton'
import { LanguageSelector } from '../components/LanguageSelector'

interface AuthConfig {
  cbe_base_url: string
  client_id: string
  tenant_id: string
  callback_host?: string | null
}

export function Login() {
  const { t } = useTranslation()
  const { didLogOut, didExpire, didAuthFailed } = useAuth()
  const [config, setConfig] = useState<AuthConfig | null>(null)
  const [configError, setConfigError] = useState(false)

  useEffect(() => {
    const tenantFromUrl = getTenantFromHostname()
    const url = tenantFromUrl
      ? `/api/v1/auth/config?tenant_id=${encodeURIComponent(tenantFromUrl)}`
      : '/api/v1/auth/config'
    fetch(url)
      .then(async (resp) => {
        if (!resp.ok) { setConfigError(true); return }
        const data = (await resp.json()) as AuthConfig
        if (!data.tenant_id) { setConfigError(true); return }
        setConfig(data)
      })
      .catch(() => setConfigError(true))
  }, [])

  // A login started on a tenant subdomain sends CBE straight back to that same
  // host, so the tenant stays recoverable from the hostname alone. Routing it
  // through the common host instead (AC8's original pin) loses the tenant: the
  // trusted-handoff callback CBE produces for this flow carries only
  // `handoff_code` — no `tenant_id` — leaving OAuthCallback nothing to forward
  // with, so it falls through to the login page. CBE's `idp.yml` redirect_uris
  // allowlist accepts the tenant host (verified against dev/fra).
  //
  // The AC8 pin still applies when the hostname holds no tenant (the common
  // host itself, or local dev): there CBE's own portal-button callback does
  // supply `tenant_id`, and OAuthCallback forwards on it.
  const portSuffix = window.location.port ? `:${window.location.port}` : ''
  const callbackHost = getTenantFromHostname()
    ? window.location.hostname
    : config?.callback_host
  const redirectUri = callbackHost
    ? `${window.location.protocol}//${callbackHost}${portSuffix}/oauth2/callback`
    : `${window.location.origin}/oauth2/callback`

  const handoffUrl = config
    ? `${config.cbe_base_url}/?client_id=${encodeURIComponent(config.client_id)}&redirect_uri=${encodeURIComponent(redirectUri)}#handoffoauth`
    : null

  return (
    <div className="login-screen">
      <div className="login-controls">
        <ThemeToggleButton />
        <LanguageSelector />
      </div>

      <div className="login-logo">
        <img src="/mambu-logo.png" alt="Mambu" />
      </div>

      <div className="login-card">
        <h2>{t('login.brand')}</h2>
        <div className="login-brand">{t('login.brandSub')}</div>
        {didExpire && (
          <div className="login-signed-out">
            {t('login.sessionExpired')}
          </div>
        )}
        {didAuthFailed && !didExpire && (
          <div className="login-auth-failed">
            {t('login.authFailed')}
          </div>
        )}
        {didLogOut && !didExpire && !didAuthFailed && (
          <div className="login-signed-out">
            {t('login.signedOut')}
          </div>
        )}
        <div className="login-subtitle">
          {didAuthFailed && !didExpire ? t('login.authFailedSubtitle') : t('login.subtitle')}
        </div>

        {configError ? (
          <div className="login-error">{t('login.configError')}</div>
        ) : !config ? (
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{t('login.loading')}</div>
        ) : (
          <a href={handoffUrl!} className="btn btn-primary">
            <LoginIcon />
            {t('login.signInWithMambu')}
          </a>
        )}
      </div>
    </div>
  )
}
